import { Reducer } from 'redux';

import { mapIdToData } from '../../../utils';
import {
  ADD_QUESTION_TOPIC_FAILED,
  ADD_QUESTION_TOPIC_REQUEST,
  ADD_QUESTION_TOPIC_SUCCESS,
  ADD_TOPIC_FAILED,
  ADD_TOPIC_REQUEST,
  ADD_TOPIC_SUCCESS,
  DELETE_QUESTION_FAILED,
  DELETE_QUESTION_REQUEST,
  DELETE_QUESTION_SUCCESS,
  DELETE_TOPIC_FAILED,
  DELETE_TOPIC_REQUEST,
  DELETE_TOPIC_SUCCESS,
  EDIT_QUESTION_FAILED,
  EDIT_QUESTION_REQUEST,
  EDIT_QUESTION_SUCCESS,
  EDIT_TOPIC_FAILED,
  EDIT_TOPIC_REQUEST,
  EDIT_TOPIC_SUCCESS,
  FETCH_SINGLE_TOPIC_FAILED,
  FETCH_SINGLE_TOPIC_REQUEST,
  FETCH_SINGLE_TOPIC_SUCCESS,
  GET_ALL_QUESTIONS_FAILED,
  GET_ALL_QUESTIONS_REQUEST,
  GET_ALL_QUESTIONS_SUCCESS,
  GET_TOPIC_QUESTIONS_FAILED,
  GET_TOPIC_QUESTIONS_REQUEST,
  GET_TOPIC_QUESTIONS_SUCCESS,
  GET_TOPICS_FAILED,
  GET_TOPICS_REQUEST,
  GET_TOPICS_SUCCESS,
  RESET_TOPICS,
} from '../constants/actionTypes';
import { Topic } from '../../../interfaces/topic.interface';
import { Question } from '../../../interfaces/question.interface';
import { ThunkFailAction, ThunkSuccessAction } from '../../../services/apiMiddleware';
import { StoreI } from '../../../rootReducer';
import { PaginationI } from '../../../interfaces/pagination.interface';
import {
  GET_TOPICS_FOR_SURVEY_FAILED,
  GET_TOPICS_FOR_SURVEY_REQUEST,
  GET_TOPICS_FOR_SURVEY_SUCCESS,
} from '../../surveys/constants/actionTypes';

export type TopicQuestionMap = Record<string, Record<string, boolean>>;

export interface TopicsState {
  loading: boolean;
  loadingAdd: boolean;
  loadingEdit: boolean;
  loadingDel: boolean;
  loadingQuestions: boolean;
  loadingAddQuestion: boolean;
  loadingEditQuestion: boolean;
  loadingDelQuestion: boolean;
  loadingQuestionsAll: boolean;
  questionsById: Record<string, Question>;
  error: any;
  byId: Record<string, Topic>;
  currentQuestions: Record<string, Question>;
  pagination: PaginationI | null;
  surveyTopics: {
    loading: boolean;
    error: Record<string, any> | null;
    byId: Record<string, Topic> | null;
    topicSelectMap: TopicQuestionMap | null;
  };
  currentTopic: Topic | null;
}

export function getInitialTopicsState(): TopicsState {
  return {
    loading: false,
    loadingAdd: false,
    loadingEdit: false,
    loadingDel: false,
    loadingQuestions: false,
    loadingAddQuestion: false,
    loadingEditQuestion: false,
    loadingDelQuestion: false,
    loadingQuestionsAll: false,
    questionsById: {},
    error: {},
    byId: {},
    currentQuestions: {},
    pagination: null,
    surveyTopics: {
      loading: false,
      error: null,
      byId: null,
      topicSelectMap: null,
    },
    currentTopic: null,
  };
}

function addTopic(state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) {
  const { response } = action;
  const { data } = response;
  return {
    ...state.byId,
    [data.id]: data,
  };
}

function addQuestion(state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) {
  const { response } = action;
  const { data } = response;

  return {
    ...state.currentQuestions,
    [data.id]: data,
  };
}

function deleteQuestionOfTopic(state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) {
  const questionsCopy = { ...state.currentQuestions };
  delete questionsCopy[action.questionId];
  return questionsCopy;
}

function deleteTopic(state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) {
  const { topicId } = action;
  const stateCopy = { ...state };
  delete stateCopy.byId[topicId];

  return stateCopy;
}

const deleteQuestionFromQuestionList = (state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) => {
  const tmpQuestionsList = { ...state.questionsById };
  delete tmpQuestionsList[action.questionId];
  return tmpQuestionsList;
};

const addQuestionToList = (state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) => {
  const topic = state.byId[action.topicId];
  if (topic.questions) {
    topic.questions.push(action.response.data);
  } else {
    topic.questions = [action.response.data];
  }
  topic.questionCount++;
  return {
    ...state.byId,
    [action.topicId]: topic,
  };
};

const deleteQuestionFromList = (state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) => {
  const topic = state.byId[action.topicId];
  if (topic.questions) {
    topic.questions = topic.questions.filter(question => action.questionId !== question.id);
    topic.questionCount--;
  }
  return {
    ...state.byId,
    [action.topicId]: topic,
  };
};

const editCustomQuestion = (state: TopicsState, action: ThunkSuccessAction | ThunkFailAction) => {
  const editedQuestion = action.response.data;
  const customTopic = state.byId[editedQuestion.topicId];
  if (customTopic.questions) {
    return {
      ...state.byId,
      [customTopic.id]: {
        ...customTopic,
        questions: customTopic.questions.map(question => {
          if (question.id === editedQuestion.id) {
            return {
              ...question,
              ...editedQuestion,
            };
          } else {
            return question;
          }
        }),
      },
    };
  }
  return state.byId;
};

// Selectors
export const getTopics = ({ topics }: StoreI) => Object.values(topics.byId);

// Selectors
export function getTopic(state: StoreI, id: string) {
  return state.topics.byId[id];
}

const topicsReducer: Reducer<TopicsState, ThunkSuccessAction | ThunkFailAction> = (
  state = getInitialTopicsState(),
  action,
) => {
  switch (action.type) {
    case GET_TOPICS_REQUEST: {
      return { ...state, loading: true };
    }
    case GET_TOPICS_SUCCESS: {
      return {
        ...state,
        loading: false,
        byId: mapIdToData(action.response.data.items),
        pagination: action.response.data.pagination,
      };
    }
    case GET_TOPICS_FAILED: {
      return { ...state, loading: false, error: action.payload };
    }
    case FETCH_SINGLE_TOPIC_REQUEST: {
      return { ...state, loading: true };
    }
    case FETCH_SINGLE_TOPIC_SUCCESS: {
      const topic = action.response.data as Topic;
      return {
        ...state,
        loading: false,
        byId: {
          ...state.byId,
          [topic.id]: topic,
        },
        currentTopic: topic,
      };
    }
    case FETCH_SINGLE_TOPIC_FAILED: {
      return { ...state, loading: false, error: action.payload };
    }
    case GET_ALL_QUESTIONS_REQUEST: {
      return { ...state, loadingQuestionsAll: true };
    }
    case GET_ALL_QUESTIONS_SUCCESS: {
      return {
        ...state,
        loadingQuestionsAll: false,
        questionsById: mapIdToData(action.response.data.items),
      };
    }
    case GET_ALL_QUESTIONS_FAILED: {
      return { ...state, loadingQuestionsAll: false, error: action.payload };
    }
    case GET_TOPIC_QUESTIONS_REQUEST: {
      return { ...state, loadingQuestions: true };
    }
    case GET_TOPIC_QUESTIONS_SUCCESS: {
      return {
        ...state,
        loadingQuestions: false,
        currentQuestions: mapIdToData(action.response.data),
      };
    }
    case GET_TOPIC_QUESTIONS_FAILED: {
      return { ...state, loadingQuestions: false, error: action.payload };
    }
    case ADD_TOPIC_REQUEST:
      return { ...state, loadingAdd: true };

    case ADD_TOPIC_SUCCESS: {
      return {
        ...state,
        byId: addTopic(state, action),
        loadingAdd: false,
        surveyTopics: {
          ...state.surveyTopics,
          topicSelectMap: mapSelectedQuestions(state, state.surveyTopics.byId, addTopic(state, action)),
        },
      };
    }
    case ADD_TOPIC_FAILED:
      return { ...state, loadingAdd: false };

    case EDIT_TOPIC_REQUEST:
      return { ...state, loadingEdit: true };
    case EDIT_TOPIC_SUCCESS:
      return {
        ...state,
        byId: { ...state.byId, [action.topicId]: action.response.data },
        loadingEdit: false,
      };
    case EDIT_TOPIC_FAILED:
      return { ...state, loadingEdit: false };
    case ADD_QUESTION_TOPIC_REQUEST:
      return { ...state, loadingAddQuestion: true };
    case ADD_QUESTION_TOPIC_SUCCESS: {
      return {
        ...state,
        loadingAddQuestion: false,
        currentQuestions: addQuestion(state, action),
        questionsById: {
          ...state.questionsById,
          [action.response.data.id]: action.response.data,
        },
        byId: addQuestionToList(state, action),
      };
    }
    case ADD_QUESTION_TOPIC_FAILED:
      return { ...state, loadingAddQuestion: false };
    case DELETE_QUESTION_REQUEST:
      return { ...state, loadingDelQuestion: true };
    case DELETE_QUESTION_SUCCESS: {
      return {
        ...state,
        loadingDelQuestion: false,
        currentQuestions: deleteQuestionOfTopic(state, action),
        questionsById: deleteQuestionFromQuestionList(state, action),
        byId: deleteQuestionFromList(state, action),
      };
    }
    case DELETE_QUESTION_FAILED:
      return { ...state, loadingDelQuestion: false };
    case EDIT_QUESTION_REQUEST:
      return { ...state, loadingEditQuestion: true };
    case EDIT_QUESTION_SUCCESS: {
      return {
        ...state,
        loadingEditQuestion: false,
        currentQuestions: {
          ...state.currentQuestions,
          [action.response.data.id]: action.response.data,
        },
        questionsById: {
          ...state.questionsById,
          [action.response.data.id]: action.response.data,
        },
        byId: editCustomQuestion(state, action),
      };
    }
    case EDIT_QUESTION_FAILED:
      return { ...state, loadingEditQuestion: false };
    case DELETE_TOPIC_REQUEST: {
      return { ...state, loadingDel: true };
    }
    case DELETE_TOPIC_SUCCESS: {
      return { ...deleteTopic(state, action), loadingDel: false };
    }
    case DELETE_TOPIC_FAILED: {
      return { ...state, loadingDel: false };
    }
    case RESET_TOPICS: {
      return { ...state, byId: {} };
    }
    case GET_TOPICS_FOR_SURVEY_REQUEST: {
      return {
        ...state,
        surveyTopics: {
          ...state.surveyTopics,
          loading: true,
        },
      };
    }
    case GET_TOPICS_FOR_SURVEY_SUCCESS: {
      return {
        ...state,
        surveyTopics: {
          ...state.surveyTopics,
          loading: false,
          byId: addSurveyTopics(action),
          topicSelectMap: mapSelectedQuestions(state, addSurveyTopics(action)),
        },
      };
    }
    case GET_TOPICS_FOR_SURVEY_FAILED: {
      return {
        ...state,
        surveyTopics: {
          ...state.surveyTopics,
          loading: true,
          error: action.payload,
        },
      };
    }
    default:
      return state;
  }
};

const addSurveyTopics = (action: ThunkSuccessAction | ThunkFailAction) => {
  const {
    response: { data },
  } = action;
  return data.reduce((acc: { [key: string]: Topic }, item: Topic) => {
    acc[item.id] = item;
    return acc;
  }, {});
};

function mapSelectedQuestions(
  state: TopicsState,
  surveyTopics: { [key: string]: Topic } | null,
  responseTopics?: { [key: string]: Topic },
) {
  const questions: TopicQuestionMap = {};
  const topics = responseTopics ? Object.values(responseTopics) : Object.values(state.byId);
  // --_____________________________________________________________________________-->
  // initialize everything with false values, otherwise _isEqual will fail on the selectedValues
  topics.forEach(topic => {
    questions[topic.id] = {};
    if (topic.questions) {
      topic.questions.forEach(question => {
        questions[topic.id][question.id] = false;
      });
    }
  });
  // --_____________________________________________________________________________-->
  // now apply the survey topic+question selection setting selected items to true
  if (surveyTopics) {
    Object.values(surveyTopics).forEach(topic => {
      if (topic.questions) {
        topic.questions.forEach((question: Question) => {
          if (questions && questions[topic.id]) {
            questions[topic.id][question.id] = true;
          }
        });
      }
    });
  }
  return questions;
}

export default topicsReducer;
